import React, { useState, useEffect } from 'react';
import {
  StyleSheet,
  View,
  Text,
  TouchableOpacity,
  FlatList,
  Alert,
  Dimensions,
  TextInput,
  ScrollView,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation, useRoute } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { AssignmentStackParamList } from '../../navigation/AssignmentNavigator';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { BilingualAssignmentForm } from '../../components/forms/BilingualAssignmentForm';
import { ResponsiveTable } from '../../components/ui/ResponsiveTable';
import { Vehicle, Driver } from '../../types';
import { useVehicleStore } from '../../stores/vehicleStore';
import { useDriverStore } from '../../stores/driverStore';
import { useAssignmentFormStore } from '../../stores/assignmentFormStore';
import { useAssignmentStore } from '../../stores/assignmentStore';
import { useAssignmentFlowStore } from '../../stores/assignmentFlowStore';
import theme from '../../theme';

type FormType = 'delivery' | 'receiving' | 'swap';

type AssignmentNavigationProp = StackNavigationProp<AssignmentStackParamList, 'AssignmentMain'>;

interface AssignmentScreenProps {
  onNavigateToHistory?: () => void;
}

export const AssignmentScreen: React.FC<AssignmentScreenProps> = ({
  onNavigateToHistory
}) => {
  const navigation = useNavigation<AssignmentNavigationProp>();
  const route = useRoute();
  const routeParams = route.params as any;
  
  const [currentView, setCurrentView] = useState<'selection' | 'form' | 'partnerOptions'>('selection');
  const [formType, setFormType] = useState<FormType>('delivery');
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [selectedDriver, setSelectedDriver] = useState<Driver | null>(null);
  const [selectedSecondaryDriver, setSelectedSecondaryDriver] = useState<Driver | null>(null);
  const [isSharedAssignment, setIsSharedAssignment] = useState<boolean>(false);
  const [showVehicleList, setShowVehicleList] = useState(false);
  const [showDriverList, setShowDriverList] = useState(false);
  const [showSecondaryDriverList, setShowSecondaryDriverList] = useState(false);
  const [vehicleSearchQuery, setVehicleSearchQuery] = useState('');
  const [driverSearchQuery, setDriverSearchQuery] = useState('');
  const [secondaryDriverSearchQuery, setSecondaryDriverSearchQuery] = useState('');
  const [swapStep, setSwapStep] = useState<'current' | 'new' | null>(null);
  const [currentVehicle, setCurrentVehicle] = useState<Vehicle | null>(null);
  const [newVehicle, setNewVehicle] = useState<Vehicle | null>(null);

  const vehicles = useVehicleStore((state) => state.vehicles);
  const drivers = useDriverStore((state) => state.drivers);
  const fetchVehicles = useVehicleStore((state) => state.fetchVehicles);
  const fetchDrivers = useDriverStore((state) => state.fetchDrivers);
  const assignments = useAssignmentStore((state) => state.assignments);
  const fetchAssignments = useAssignmentStore((state) => state.fetchAssignments);
  const submitDeliveryForm = useAssignmentFormStore((state) => state.submitDeliveryForm);
  const submitReceivingForm = useAssignmentFormStore((state) => state.submitReceivingForm);
  const submitSwapForm = useAssignmentFormStore((state) => state.submitSwapForm);
  
  // Assignment flow store for cross-section navigation
  const pendingAssignment = useAssignmentFlowStore((state) => state.pendingAssignment);
  const clearPendingAssignment = useAssignmentFlowStore((state) => state.clearPendingAssignment);

  useEffect(() => {
    fetchVehicles();
    fetchDrivers();
    fetchAssignments();
  }, [fetchVehicles, fetchDrivers, fetchAssignments]);

  // Handle pre-selection from 3-dots menu (Vehicle/Driver list screens)
  useEffect(() => {
    // Check for pre-selection data from either route params (mobile) or sessionStorage (web)
    let preSelectionData = routeParams || {};
    
    if (Platform.OS === 'web' && typeof sessionStorage !== 'undefined') {
      try {
        const storedPreSelection = sessionStorage.getItem('assignmentPreSelection');
        if (storedPreSelection) {
          preSelectionData = JSON.parse(storedPreSelection);
          // Clear it after reading so it doesn't persist
          sessionStorage.removeItem('assignmentPreSelection');
          console.log('📱 Web: Read pre-selection from sessionStorage:', preSelectionData);
        }
      } catch (error) {
        console.error('Error reading sessionStorage:', error);
      }
    }
    
    if (preSelectionData?.preSelectedVehicleId && vehicles.length > 0) {
      const vehicle = vehicles.find(v => v.id === preSelectionData.preSelectedVehicleId);
      if (vehicle) {
        console.log('🚗 Pre-selecting vehicle from 3-dots menu:', vehicle.licensePlate);
        setSelectedVehicle(vehicle);
        if (preSelectionData.preSelectedFormType) {
          setFormType(preSelectionData.preSelectedFormType);
        }
      }
    }
    
    if (preSelectionData?.preSelectedDriverId && drivers.length > 0) {
      const driver = drivers.find(d => d.id === preSelectionData.preSelectedDriverId);
      if (driver) {
        console.log('👤 Pre-selecting driver from 3-dots menu:', driver.name);
        setSelectedDriver(driver);
        if (preSelectionData.preSelectedFormType) {
          setFormType(preSelectionData.preSelectedFormType);
        }
      }
    }
  }, [routeParams, vehicles, drivers]);

  // Check for pending assignment from job card workflow
  useEffect(() => {
    if (pendingAssignment && drivers.length > 0) {
      console.log('🎯 Pending assignment detected in AssignmentScreen:', pendingAssignment);
      console.log('📋 Drivers loaded:', drivers.length);
      
      const { vehicle, assignmentData } = pendingAssignment;
      
      // Set form type to receiving
      setFormType('receiving');
      
      // Set the vehicle
      setSelectedVehicle(vehicle);
      
      // Find and set the primary driver
      const driver = drivers.find(d => d.id === assignmentData.driverId);
      if (driver) {
        console.log('✅ Found driver:', driver.name);
        setSelectedDriver(driver);
      } else {
        console.warn('⚠️ Driver not found:', assignmentData.driverId, 'Available drivers:', drivers.map(d => d.id));
      }
      
      // Find and set secondary driver if it exists
      if (assignmentData.secondaryDriverId) {
        const secondaryDriver = drivers.find(d => d.id === assignmentData.secondaryDriverId);
        if (secondaryDriver) {
          console.log('✅ Found secondary driver:', secondaryDriver.name);
          setSelectedSecondaryDriver(secondaryDriver);
        }
      }
      
      // Set shared assignment status
      setIsSharedAssignment(assignmentData.isSharedAssignment || false);
      
      // Go directly to form view
      console.log('🚀 Auto-opening receiving form');
      setCurrentView('form');
    } else if (pendingAssignment && drivers.length === 0) {
      console.log('⏳ Pending assignment detected but drivers not loaded yet...');
    }
  }, [pendingAssignment, drivers]);

  // Handle route parameters for completing assignments from history
  useEffect(() => {
    if (routeParams?.mode === 'receiving' && routeParams?.assignmentData) {
      const { vehicleId, driverId, secondaryDriverId, isSharedAssignment: isShared } = routeParams.assignmentData;
      
      console.log('Setting up receiving mode for assignment:', routeParams.assignmentData);
      
      // Set form type to receiving
      setFormType('receiving');
      
      // Find and set the vehicle
      const vehicle = vehicles.find(v => v.id === vehicleId);
      if (vehicle) {
        setSelectedVehicle(vehicle);
      }
      
      // Find and set the primary driver
      const driver = drivers.find(d => d.id === driverId);
      if (driver) {
        setSelectedDriver(driver);
      }
      
      // Find and set secondary driver if it exists
      if (secondaryDriverId) {
        const secondaryDriver = drivers.find(d => d.id === secondaryDriverId);
        if (secondaryDriver) {
          setSelectedSecondaryDriver(secondaryDriver);
        }
      }
      
      // Set shared assignment status
      setIsSharedAssignment(isShared);
      
      // Go directly to form view
      setCurrentView('form');
    }
  }, [routeParams, vehicles, drivers]);

  // Get active assignments to cross-check vehicle and driver availability
  // NOTE: Only check 'active' status - drivers with 'pending_settlement' can take new vehicles
  const activeAssignments = assignments.filter(a => a.status === 'active');
  const vehiclesWithActiveAssignments = new Set(
    activeAssignments.map(a => a.vehicle_id).filter(Boolean)
  );
  const driversWithActiveAssignments = new Set(
    activeAssignments.flatMap(a => [a.primary_driver_id, a.secondary_driver_id]).filter(Boolean)
  );

  // Enhanced filtering logic that excludes vehicles/drivers with active assignments
  const availableVehicles = vehicles.filter(v => 
    !v.currentDriverId && 
    v.carStatus === 'available' &&
    !vehiclesWithActiveAssignments.has(v.id)
  );

  console.log('🚗 AssignmentScreen - Enhanced vehicle filtering:');
  console.log('🚗 Total vehicles:', vehicles.length);
  console.log('🚗 Vehicles with active assignments:', vehiclesWithActiveAssignments.size);
  console.log('🚗 Available vehicles (after enhanced filtering):', availableVehicles.length);

  const assignedVehicles = vehicles.filter(v => 
    v.currentDriverId && 
    v.carStatus === 'work'
  );

  // Additional analysis for debugging
  const vehiclesByStatus = vehicles.reduce((acc, vehicle) => {
    const status = vehicle.carStatus || 'undefined';
    if (!acc[status]) acc[status] = { total: 0, withDriver: 0, withoutDriver: 0 };
    acc[status].total++;
    if (vehicle.currentDriverId) {
      acc[status].withDriver++;
    } else {
      acc[status].withoutDriver++;
    }
    return acc;
  }, {} as Record<string, {total: number, withDriver: number, withoutDriver: number}>);

  const unaccountedVehicles = vehicles.filter(v => {
    const isAvailable = !v.currentDriverId && v.carStatus === 'available';
    const isAssigned = v.currentDriverId && v.carStatus === 'work';
    return !isAvailable && !isAssigned;
  });

  // Debug detailed vehicle status for troubleshooting
  if (vehicles.length > 0) {
    console.log('🔍 Vehicle status breakdown:');
    
    // Find vehicle 70515 specifically
    const vehicle70515 = vehicles.find(v => v.licensePlate === '70515');
    if (vehicle70515) {
      console.log('🚨 Vehicle 70515 status:', {
        id: vehicle70515.id,
        licensePlate: vehicle70515.licensePlate,
        carStatus: vehicle70515.carStatus,
        currentDriverId: vehicle70515.currentDriverId,
        assignedDriverId: vehicle70515.assignedDriverId,
        isInAvailableList: availableVehicles.some(v => v.licensePlate === '70515'),
        isInAssignedList: assignedVehicles.some(v => v.licensePlate === '70515')
      });
      
      // Removed automatic sync logic to prevent fluctuating numbers
    }
    
    // Show first 5 vehicles for general debugging
    vehicles.slice(0, 5).forEach(v => {
      console.log(`Vehicle ${v.licensePlate}: status=${v.carStatus}, currentDriverId=${v.currentDriverId || 'none'}`);
    });
  }

  // Simple debug logging
  console.log('🚗 AssignmentScreen - Total vehicles:', vehicles.length);
  console.log('🚗 AssignmentScreen - Available vehicles:', availableVehicles.length);
  console.log('🚗 AssignmentScreen - Assigned vehicles:', assignedVehicles.length);
  
  // Clean and simple - just show basic status breakdown
  console.log('📊 Vehicle Status:', {
    available: vehiclesByStatus.available?.total || 0,
    work: vehiclesByStatus.work?.total || 0,
    in_workshop: vehiclesByStatus.in_workshop?.total || 0,
    other: vehicles.length - (vehiclesByStatus.available?.total || 0) - (vehiclesByStatus.work?.total || 0) - (vehiclesByStatus.in_workshop?.total || 0)
  });

  // Simple manual refresh function
  const handleManualRefresh = async () => {
    console.log('🔄 Refreshing data...');
    try {
      await Promise.all([
        fetchVehicles(),
        fetchDrivers(), 
        fetchAssignments()
      ]);
      console.log('✅ Data refreshed');
    } catch (error) {
      console.error('❌ Refresh failed:', error);
    }
  };

  const handleFormTypeSelection = (type: FormType) => {
    setFormType(type);
    if (type === 'delivery') {
      // For delivery, we need an available vehicle
      setSelectedVehicle(null);
      setSelectedDriver(null);
      setShowVehicleList(true);
    } else if (type === 'receiving') {
      // For receiving, we need an assigned vehicle
      setSelectedVehicle(null);
      setSelectedDriver(null);
      setShowVehicleList(true);
    } else if (type === 'swap') {
      // Navigate to new swap selection screen
      navigation.navigate('VehicleSwapSelection');
    }
  };

  const handleVehicleSelection = (vehicle: Vehicle) => {
    if (formType === 'swap') {
      if (swapStep === 'current') {
        // First step: select current vehicle (must be assigned)
        if (!vehicle.currentDriverId) {
          Alert.alert('خطأ / Error', 'يجب اختيار مركبة مُعيَّنة حالياً / Must select a currently assigned vehicle');
          return;
        }
        setCurrentVehicle(vehicle);
        setSelectedDriver(drivers.find(d => d.id === vehicle.currentDriverId) || null);
        setSwapStep('new');
        setShowVehicleList(true); // Stay in vehicle list for new vehicle selection
        setVehicleSearchQuery(''); // Reset search
      } else if (swapStep === 'new') {
        // Second step: select new vehicle (must be available)
        if (vehicle.currentDriverId) {
          Alert.alert('خطأ / Error', 'يجب اختيار مركبة متاحة / Must select an available vehicle');
          return;
        }
        if (vehicle.id === currentVehicle?.id) {
          Alert.alert('خطأ / Error', 'لا يمكن تبديل المركبة بنفسها / Cannot swap vehicle with itself');
          return;
        }
        setNewVehicle(vehicle);
        setShowVehicleList(false);
        setCurrentView('form');
      }
    } else {
      // Normal flow for delivery/receiving
      setSelectedVehicle(vehicle);
      setShowVehicleList(false);
      
      // If vehicle is assigned, auto-select the assigned driver
      if (vehicle.currentDriverId) {
        const assignedDriver = drivers.find(d => d.id === vehicle.currentDriverId);
        if (assignedDriver) {
          setSelectedDriver(assignedDriver);
          setCurrentView('form');
        } else {
          setShowDriverList(true);
        }
      } else {
        setShowDriverList(true);
      }
    }
  };

  const handleDriverSelection = (driver: Driver) => {
    setSelectedDriver(driver);
    setShowDriverList(false);
    // Show partner assignment options instead of going directly to form
    setCurrentView('partnerOptions');
  };

  const handleFormSubmit = async (formData: any) => {
    try {
      let result;
      
      if (formType === 'delivery') {
        if (!selectedVehicle || !selectedDriver) {
          Alert.alert('خطأ / Error', 'لم يتم اختيار مركبة أو سائق / No vehicle or driver selected');
          return;
        }
        result = await submitDeliveryForm(
          formData, 
          selectedVehicle.id, 
          selectedDriver.id, 
          selectedSecondaryDriver?.id || null, 
          isSharedAssignment
        );
      } else if (formType === 'receiving') {
        if (!selectedVehicle || !selectedDriver) {
          Alert.alert('خطأ / Error', 'لم يتم اختيار مركبة أو سائق / No vehicle or driver selected');
          return;
        }
        result = await submitReceivingForm(formData, selectedVehicle.id, selectedDriver.id);
      } else if (formType === 'swap') {
        // For swap, we need both current and new vehicles
        if (!currentVehicle || !newVehicle || !selectedDriver) {
          Alert.alert('خطأ / Error', 'يجب اختيار المركبتين والسائق للتبديل / Both vehicles and driver must be selected for swap');
          return;
        }
        result = await submitSwapForm(formData, currentVehicle.id, newVehicle.id, selectedDriver.id);
      }

      // Only show success if we reach this point (no exception thrown)
      Alert.alert(
        'نجح التسليم / Success',
        `تم تسليم النموذج بنجاح\nرقم المرجع: ${formData.referenceNumber}\n\nForm submitted successfully\nReference: ${formData.referenceNumber}`,
        [
          {
            text: 'موافق / OK',
            onPress: () => {
              // Check if we have a pending assignment (from job card workflow)
              if (pendingAssignment && pendingAssignment.returnToJobCard) {
                console.log('✅ Receiving form completed - returning to job card creation (from pending assignment)');
                
                const vehicleToReturn = pendingAssignment.vehicle;
                
                // NOTE: We DON'T clear the pending assignment yet!
                // The job card screen will clear it after the vehicle is selected
                // This allows the store to persist across navigation
                
                // Navigate back to job card creation
                if (Platform.OS === 'web') {
                  console.log('🌐 Web: Navigating to /maintenance');
                  // On web, navigate to maintenance route
                  // The job card screen will check the store and select the vehicle
                  window.location.href = '/maintenance';
                } else {
                  navigation.navigate('Maintenance' as any, {
                    screen: 'AddJobCard',
                    params: {
                      selectedVehicle: vehicleToReturn,
                    }
                  });
                  // Clear after navigating on mobile (params carry the data)
                  clearPendingAssignment();
                }
                return;
              }
              
              // Legacy: Check route params for returnToJobCard (old method)
              if (routeParams?.returnToJobCard && selectedVehicle) {
                console.log('✅ Receiving form completed - navigating to job card creation (legacy route params)');
                // Navigate to job card creation with the vehicle
                navigation.navigate('Maintenance' as any, {
                  screen: 'AddJobCard',
                  params: {
                    selectedVehicle: selectedVehicle,
                  }
                });
                return;
              }
              
              // Clear form state first
              setCurrentView('selection');
              setSelectedVehicle(null);
              setSelectedDriver(null);
              setCurrentVehicle(null);
              setNewVehicle(null);
              setSwapStep(null);
              
              // Refresh all data to ensure status updates are reflected
              setTimeout(() => {
                fetchAssignments(); // Refresh assignments to show the new assignment
                fetchVehicles(); // Refresh vehicles to show updated status
                fetchDrivers(); // Refresh drivers to show updated status
              }, 500); // Give more time for database updates to propagate
            }
          }
        ],
        { cancelable: false } // Prevent dismissing without action
      );
    } catch (error: any) {
      console.error('❌ Assignment form submission failed:', error);
      Alert.alert('خطأ / Error', `حدث خطأ أثناء تسليم النموذج\n${error.message}\n\nError submitting form\n${error.message}`);
      // Re-throw the error so the form can handle it properly
      throw error;
    }
  };

  const handleFormCancel = () => {
    setCurrentView('selection');
    setSelectedVehicle(null);
    setSelectedDriver(null);
  };

  const handlePrint = () => {
    // This is now handled internally by the form component
    return;
  };

  const renderVehicleItem = ({ item }: { item: Vehicle }) => {
    const driver = item.currentDriverId ? drivers.find(d => d.id === item.currentDriverId) : null;
    
    return (
      <TouchableOpacity
        style={styles.vehicleCard}
        onPress={() => handleVehicleSelection(item)}
      >
        <View style={styles.vehicleCardContent}>
          <View style={styles.vehicleMainInfo}>
            <Text style={styles.vehiclePlate}>{item.licensePlate}</Text>
            <Text style={styles.vehicleMakeModel}>
              {item.makeModel || 'Unknown Make/Model'} - {item.year || 'N/A'}
            </Text>
          </View>
          
          <View style={styles.vehicleStatusInfo}>
            <View style={[styles.statusBadge, { backgroundColor: item.currentDriverId ? theme.colors.error : theme.colors.success }]}>
              <Text style={styles.statusText}>
                {item.currentDriverId ? 'مُعيَّن / Assigned' : 'متاح / Available'}
              </Text>
            </View>
            
            {driver && (
              <Text style={styles.driverInfo}>
                السائق / Driver: {driver.name}
              </Text>
            )}
          </View>
          
          <Ionicons name="chevron-forward" size={24} color={theme.colors.textSecondary} />
        </View>
      </TouchableOpacity>
    );
  };

  const renderDriverItem = ({ item }: { item: Driver }) => (
    <TouchableOpacity
      style={styles.listItem}
      onPress={() => handleDriverSelection(item)}
    >
      <View style={styles.itemContent}>
        <View style={styles.itemInfo}>
          <Text style={styles.itemTitle}>{item.name}</Text>
          <Text style={styles.itemSubtitle}>{item.phone}</Text>
          <Text style={styles.itemStatus}>
            رقم الرخصة / License: {item.licenseNumber}
          </Text>
        </View>
        <Ionicons name="chevron-forward" size={24} color={theme.colors.textSecondary} />
      </View>
    </TouchableOpacity>
  );

  if (currentView === 'form') {
    // For swap, we need to determine which vehicle to show based on the swap step
    let formVehicle = selectedVehicle;
    let formDriver = selectedDriver;
    
    if (formType === 'swap') {
      // For swap, show the new vehicle and keep the driver from current vehicle
      formVehicle = newVehicle || currentVehicle;
      formDriver = selectedDriver; // This should be the driver from the current vehicle
    }
    
    return (
      <BilingualAssignmentForm
        formType={formType}
        vehicle={formVehicle || undefined}
        driver={formDriver || undefined}
        secondaryDriver={selectedSecondaryDriver || undefined}
        isSharedAssignment={isSharedAssignment}
        currentVehicle={currentVehicle || undefined}
        newVehicle={newVehicle || undefined}
        onSubmit={handleFormSubmit}
        onCancel={handleFormCancel}
        onPrint={handlePrint}
      />
    );
  }

  if (showVehicleList) {
    let vehiclesToShow: Vehicle[] = [];
    let headerTitle = 'اختر المركبة / Select Vehicle';
    
    if (formType === 'swap') {
      if (swapStep === 'current') {
        vehiclesToShow = assignedVehicles;
        headerTitle = 'اختر المركبة الحالية / Select Current Vehicle';
      } else if (swapStep === 'new') {
        vehiclesToShow = availableVehicles.filter(v => v.id !== currentVehicle?.id);
        headerTitle = 'اختر المركبة الجديدة / Select New Vehicle';
      }
    } else {
      vehiclesToShow = formType === 'delivery' ? availableVehicles : 
                      formType === 'receiving' ? assignedVehicles : 
                      vehicles; // For other cases, show all vehicles
    }
    
    // Filter vehicles based on search query
    const filteredVehicles = vehiclesToShow.filter(vehicle =>
      vehicle.licensePlate.toLowerCase().includes(vehicleSearchQuery.toLowerCase()) ||
      (vehicle.makeModel?.toLowerCase().includes(vehicleSearchQuery.toLowerCase()) || false)
    );

    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => {
              if (formType === 'swap' && swapStep === 'new') {
                // Go back to current vehicle selection
                setSwapStep('current');
                setNewVehicle(null);
                setVehicleSearchQuery('');
              } else {
                setShowVehicleList(false);
                setVehicleSearchQuery('');
                setSwapStep(null);
                setCurrentVehicle(null);
                setNewVehicle(null);
              }
            }}
          >
            <Ionicons name="arrow-back" size={24} color={theme.colors.textPrimary} />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>
            {headerTitle}
          </Text>
        </View>
        
        {/* Search Bar */}
        <View style={styles.searchContainer}>
          <View style={styles.searchInputContainer}>
            <Ionicons name="search" size={20} color={theme.colors.textSecondary} style={styles.searchIcon} />
            <TextInput
              style={styles.searchInput}
              placeholder="البحث برقم اللوحة أو نوع المركبة / Search by plate or make model"
              value={vehicleSearchQuery}
              onChangeText={setVehicleSearchQuery}
              placeholderTextColor={theme.colors.textSecondary}
            />
            {vehicleSearchQuery.length > 0 && (
              <TouchableOpacity
                onPress={() => setVehicleSearchQuery('')}
                style={styles.clearButton}
              >
                <Ionicons name="close-circle" size={20} color={theme.colors.textSecondary} />
              </TouchableOpacity>
            )}
          </View>
        </View>
        
        <FlatList
          data={filteredVehicles}
          renderItem={renderVehicleItem}
          keyExtractor={(item) => item.id}
          style={styles.list}
          showsVerticalScrollIndicator={true}
          bounces={true}
          removeClippedSubviews={true}
          maxToRenderPerBatch={10}
          initialNumToRender={10}
          windowSize={10}
          getItemLayout={(data, index) => ({
            length: 80, // Approximate item height
            offset: 80 * index,
            index,
          })}
          ListEmptyComponent={
            <View style={styles.emptyContainer}>
              <Ionicons name="car-outline" size={48} color={theme.colors.textSecondary} />
              <Text style={styles.emptyText}>
                {vehicleSearchQuery ? 'لم يتم العثور على مركبات / No vehicles found' : 'لا توجد مركبات متاحة / No vehicles available'}
              </Text>
            </View>
          }
        />
      </View>
    );
  }

  if (showDriverList) {
    // Filter out assigned drivers first, then apply search filter
    // ENHANCED: Check both active assignments AND currentVehicleId on driver record
    
    // DEBUG: Log all drivers for inspection
    console.log('🔍 DEBUG: Checking all drivers...');
    const debugDrivers = drivers.filter(d => 
      d.name.includes('Anass') || 
      d.name.includes('Sohbat') || 
      d.name.includes('Zubair') || 
      d.name.includes('Demo') || 
      d.name.includes('Test')
    );
    debugDrivers.forEach(d => {
      console.log(`🔍 Driver: ${d.name}`);
      console.log(`   - ID: ${d.id}`);
      console.log(`   - Status: ${d.status}`);
      console.log(`   - currentVehicleId: ${d.currentVehicleId || 'NULL'}`);
      console.log(`   - Has active assignment: ${driversWithActiveAssignments.has(d.id) ? 'YES' : 'NO'}`);
      console.log(`   - Will show: ${d.status === 'active' && !driversWithActiveAssignments.has(d.id) && !d.currentVehicleId ? '✅ YES' : '❌ NO'}`);
    });
    
    const availableDrivers = drivers.filter(driver => {
      // Must be active status
      if (driver.status !== 'active') {
        if (driver.name.includes('Anass') || driver.name.includes('Sohbat') || driver.name.includes('Zubair')) {
          console.log(`❌ ${driver.name} filtered: status is ${driver.status}, not 'active'`);
        }
        return false;
      }
      
      // Check if driver has active assignment in assignment history
      if (driversWithActiveAssignments.has(driver.id)) {
        if (driver.name.includes('Anass') || driver.name.includes('Sohbat') || driver.name.includes('Zubair')) {
          console.log(`❌ ${driver.name} filtered: has active assignment in history`);
        }
        return false;
      }
      
      // CRITICAL: Also check if driver has currentVehicleId set
      // This is the authoritative source of truth for current assignments
      if (driver.currentVehicleId) {
        console.log(`❌ Filtering out ${driver.name} - has currentVehicleId: ${driver.currentVehicleId}`);
        return false;
      }
      
      return true;
    });
    
    const filteredDrivers = availableDrivers.filter(driver =>
      driver.name.toLowerCase().includes(driverSearchQuery.toLowerCase()) ||
      driver.phone.includes(driverSearchQuery)
    );

    console.log('👨‍💼 AssignmentScreen - Enhanced driver filtering:');
    console.log('👨‍💼 Total drivers:', drivers.length);
    console.log('👨‍💼 Drivers with active assignments (from history):', driversWithActiveAssignments.size);
    console.log('👨‍💼 Available drivers (after enhanced filtering):', availableDrivers.length);
    console.log('👨‍💼 Filtered drivers (after search):', filteredDrivers.length);
    console.log('👨‍💼 ALL available drivers:', availableDrivers.map(d => ({ name: d.name, id: d.id.substring(0,8), currentVehicleId: d.currentVehicleId ? d.currentVehicleId.substring(0,8) : 'NULL' })));

    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => {
              setShowDriverList(false);
              setDriverSearchQuery('');
            }}
          >
            <Ionicons name="arrow-back" size={24} color={theme.colors.textPrimary} />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>
            اختر السائق / Select Driver
          </Text>
        </View>
        
        {/* Search Bar */}
        <View style={styles.searchContainer}>
          <View style={styles.searchInputContainer}>
            <Ionicons name="search" size={20} color={theme.colors.textSecondary} style={styles.searchIcon} />
            <TextInput
              style={styles.searchInput}
              placeholder="البحث بالاسم أو رقم الهاتف / Search by name or phone"
              value={driverSearchQuery}
              onChangeText={setDriverSearchQuery}
              placeholderTextColor={theme.colors.textSecondary}
            />
            {driverSearchQuery.length > 0 && (
              <TouchableOpacity
                onPress={() => setDriverSearchQuery('')}
                style={styles.clearButton}
              >
                <Ionicons name="close-circle" size={20} color={theme.colors.textSecondary} />
              </TouchableOpacity>
            )}
          </View>
        </View>
        
        <FlatList
          data={filteredDrivers}
          renderItem={renderDriverItem}
          keyExtractor={(item) => item.id}
          style={styles.list}
          showsVerticalScrollIndicator={true}
          bounces={true}
          removeClippedSubviews={true}
          maxToRenderPerBatch={10}
          initialNumToRender={10}
          windowSize={10}
          getItemLayout={(data, index) => ({
            length: 80, // Approximate item height
            offset: 80 * index,
            index,
          })}
          ListEmptyComponent={
            <View style={styles.emptyContainer}>
              <Ionicons name="person-outline" size={48} color={theme.colors.textSecondary} />
              <Text style={styles.emptyText}>
                {driverSearchQuery ? 'لم يتم العثور على سائقين / No drivers found' : 'لا يوجد سائقون متاحون / No drivers available'}
              </Text>
            </View>
          }
        />
      </View>
    );
  }

  if (showSecondaryDriverList) {
    // Filter out assigned drivers, active drivers only, AND exclude the selected primary driver
    // ENHANCED: Check both active assignments AND currentVehicleId for consistency
    const availableSecondaryDrivers = drivers.filter(driver => {
      // Exclude if driver is not active
      if (driver.status !== 'active') return false;
      
      // Exclude if driver is the selected primary driver
      if (driver.id === selectedDriver?.id) return false;
      
      // CRITICAL: Check if driver has currentVehicleId set (authoritative source)
      if (driver.currentVehicleId) {
        console.log(`❌ Filtering out secondary driver ${driver.name} - has currentVehicleId: ${driver.currentVehicleId}`);
        return false;
      }
      
      // Also check if driver has ANY active assignment in database (as primary OR secondary)
      const hasActiveAssignment = assignments.some(assignment => 
        assignment.status === 'active' && 
        (assignment.primary_driver_id === driver.id || assignment.secondary_driver_id === driver.id)
      );
      
      if (hasActiveAssignment) return false;
      
      return true;
    });
    
    // Apply search filter to available secondary drivers
    const filteredSecondaryDrivers = availableSecondaryDrivers.filter(driver =>
      driver.name.toLowerCase().includes(secondaryDriverSearchQuery.toLowerCase()) ||
      driver.phone.includes(secondaryDriverSearchQuery)
    );

    console.log('👥 AssignmentScreen - Secondary driver filtering:');
    console.log('👥 Total drivers:', drivers.length);
    console.log('👥 Available secondary drivers (excluding primary & assigned):', availableSecondaryDrivers.length);
    console.log('👥 Filtered secondary drivers (after search):', filteredSecondaryDrivers.length);
    console.log('👥 Primary driver excluded:', selectedDriver?.name || 'none');
    console.log('👥 Sample available:', availableSecondaryDrivers.slice(0, 5).map(d => ({ name: d.name, id: d.id })));

    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => {
              setShowSecondaryDriverList(false);
              setSecondaryDriverSearchQuery('');
            }}
          >
            <Ionicons name="arrow-back" size={24} color={theme.colors.textPrimary} />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>
            اختر السائق الثانوي / Select Secondary Driver
          </Text>
        </View>
        
        {/* Search Bar */}
        <View style={styles.searchContainer}>
          <View style={styles.searchInputContainer}>
            <Ionicons name="search" size={20} color={theme.colors.textSecondary} style={styles.searchIcon} />
            <TextInput
              style={styles.searchInput}
              placeholder="البحث بالاسم أو رقم الهاتف / Search by name or phone"
              value={secondaryDriverSearchQuery}
              onChangeText={setSecondaryDriverSearchQuery}
              placeholderTextColor={theme.colors.textSecondary}
            />
            {secondaryDriverSearchQuery.length > 0 && (
              <TouchableOpacity
                onPress={() => setSecondaryDriverSearchQuery('')}
                style={styles.clearButton}
              >
                <Ionicons name="close" size={16} color={theme.colors.textSecondary} />
              </TouchableOpacity>
            )}
          </View>
        </View>

        <FlatList
          data={filteredSecondaryDrivers}
          keyExtractor={(item) => item.id}
          style={styles.list}
          showsVerticalScrollIndicator={true}
          bounces={true}
          removeClippedSubviews={true}
          maxToRenderPerBatch={10}
          initialNumToRender={10}
          windowSize={10}
          getItemLayout={(data, index) => ({
            length: 80, // Approximate item height
            offset: 80 * index,
            index,
          })}
          renderItem={({ item }) => (
            <TouchableOpacity
              style={styles.listItem}
              onPress={() => {
                setSelectedSecondaryDriver(item);
                setShowSecondaryDriverList(false);
                setSecondaryDriverSearchQuery('');
              }}
            >
              <View style={styles.itemContent}>
                <View style={styles.itemInfo}>
                  <Text style={styles.itemTitle}>{item.name}</Text>
                  <Text style={styles.itemSubtitle}>{item.phone}</Text>
                  <Text style={styles.itemStatus}>
                    رقم الرخصة / License: {item.licenseNumber}
                  </Text>
                </View>
                <Ionicons name="chevron-forward" size={24} color={theme.colors.textSecondary} />
              </View>
            </TouchableOpacity>
          )}
          contentContainerStyle={styles.list}
          ListEmptyComponent={
            <View style={styles.emptyContainer}>
              <Ionicons name="people-outline" size={48} color={theme.colors.textSecondary} />
              <Text style={styles.emptyText}>
                {secondaryDriverSearchQuery ? 'لم يتم العثور على سائقين / No drivers found' : 'لا يوجد سائقين متاحين / No drivers available'}
              </Text>
            </View>
          }
        />
      </View>
    );
  }

  if (currentView === 'partnerOptions') {
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => {
              setCurrentView('selection');
              setSelectedDriver(null);
              setIsSharedAssignment(false);
              setSelectedSecondaryDriver(null);
            }}
          >
            <Ionicons name="arrow-back" size={24} color={theme.colors.textPrimary} />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>
            خيارات التعيين / Assignment Options
          </Text>
        </View>

        <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
          {/* Selected Driver Info */}
          <Card style={styles.section}>
            <View style={styles.sectionHeader}>
              <Ionicons name="person" size={24} color={theme.colors.blue600} />
              <Text style={styles.sectionTitle}>السائق المحدد / Selected Driver</Text>
            </View>
            <Text style={styles.itemTitle}>{selectedDriver?.name}</Text>
            <Text style={styles.itemSubtitle}>{selectedDriver?.phone}</Text>
          </Card>

          {/* Assignment Type Options */}
          <Card style={styles.section}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>
                نوع التعيين / Assignment Type
              </Text>
            </View>
            
            <View style={styles.formTypeGrid}>
              <TouchableOpacity
                style={[styles.formTypeCard, !isSharedAssignment && styles.selectedFormType]}
                onPress={() => {
                  setIsSharedAssignment(false);
                  setSelectedSecondaryDriver(null);
                }}
              >
                <View style={styles.formTypeIcon}>
                  <Ionicons name="person" size={40} color={theme.colors.blue600} />
                </View>
                <Text style={styles.formTypeTitle}>تعيين فردي</Text>
                <Text style={styles.formTypeTitleEn}>Individual Assignment</Text>
                <Text style={styles.formTypeDesc}>
                  تعيين المركبة لسائق واحد فقط
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[styles.formTypeCard, isSharedAssignment && styles.selectedFormType]}
                onPress={() => {
                  setIsSharedAssignment(true);
                }}
              >
                <View style={styles.formTypeIcon}>
                  <Ionicons name="people" size={40} color={theme.colors.orange600} />
                </View>
                <Text style={styles.formTypeTitle}>تعيين مشترك</Text>
                <Text style={styles.formTypeTitleEn}>Shared Assignment</Text>
                <Text style={styles.formTypeDesc}>
                  تعيين المركبة لسائقين كشركاء (+33%)
                </Text>
              </TouchableOpacity>
            </View>
          </Card>

          {/* Secondary Driver Selection */}
          {isSharedAssignment && (
            <Card style={styles.section}>
              <View style={styles.sectionHeader}>
                <Ionicons name="people" size={24} color={theme.colors.blue600} />
                <Text style={styles.sectionTitle}>
                  اختر السائق الثانوي / Select Secondary Driver
                </Text>
              </View>
              
              {selectedSecondaryDriver ? (
                <TouchableOpacity
                  style={styles.listItem}
                  onPress={() => setShowSecondaryDriverList(true)}
                >
                  <View style={styles.itemContent}>
                    <View style={styles.itemInfo}>
                      <Text style={styles.itemTitle}>{selectedSecondaryDriver.name}</Text>
                      <Text style={styles.itemSubtitle}>{selectedSecondaryDriver.phone}</Text>
                    </View>
                    <Text style={styles.itemSubtitle}>تغيير / Change</Text>
                  </View>
                </TouchableOpacity>
              ) : (
                <TouchableOpacity
                  style={styles.listItem}
                  onPress={() => setShowSecondaryDriverList(true)}
                >
                  <View style={styles.itemContent}>
                    <Ionicons name="add-circle-outline" size={24} color={theme.colors.blue600} />
                    <Text style={styles.itemTitle}>
                      اختر السائق الثانوي / Select Secondary Driver
                    </Text>
                  </View>
                </TouchableOpacity>
              )}
            </Card>
          )}
          {/* Continue Button */}
          <TouchableOpacity
            style={[
              styles.formTypeCard,
              (!isSharedAssignment || (isSharedAssignment && selectedSecondaryDriver)) 
                ? styles.selectedFormType 
                : { opacity: 0.5 }
            ]}
            onPress={() => {
              if (!isSharedAssignment || (isSharedAssignment && selectedSecondaryDriver)) {
                setCurrentView('form');
              }
            }}
            disabled={isSharedAssignment && !selectedSecondaryDriver}
          >
            <Text style={[
              styles.formTypeTitle,
              (!isSharedAssignment || (isSharedAssignment && selectedSecondaryDriver)) 
                ? {} 
                : { opacity: 0.7 }
            ]}>
              متابعة / Continue
            </Text>
            {isSharedAssignment && !selectedSecondaryDriver && (
              <Text style={[styles.formTypeDesc, { color: theme.colors.error }]}>
                يرجى اختيار السائق الثانوي أولاً
              </Text>
            )}
          </TouchableOpacity>
        </ScrollView>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.headerTitle}>
          نماذج التعيين / Assignment Forms
        </Text>
        <View style={{ flexDirection: 'row', gap: 8 }}>
          <TouchableOpacity 
            style={styles.refreshButton}
            onPress={handleManualRefresh}
          >
            <Ionicons name="refresh" size={20} color={theme.colors.primary} />
            <Text style={styles.refreshButtonText}>تحديث / Refresh</Text>
          </TouchableOpacity>
          {onNavigateToHistory && (
            <TouchableOpacity
              style={styles.historyButton}
              onPress={onNavigateToHistory}
            >
              <Ionicons name="time" size={20} color={theme.colors.blue600} />
              <Text style={styles.historyButtonText}>التاريخ / History</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>

      <ScrollView 
        style={styles.content}
        showsVerticalScrollIndicator={true}
        bounces={true}
        contentContainerStyle={styles.scrollContent}
      >
        {/* Form Type Selection */}
        <Card style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>
              اختر نوع النموذج / Select Form Type
            </Text>
          </View>
          
          <View style={styles.formTypeGrid}>
            <TouchableOpacity
              style={[styles.formTypeCard, formType === 'delivery' && styles.selectedFormType]}
              onPress={() => handleFormTypeSelection('delivery')}
            >
              <View style={styles.formTypeIcon}>
                <Ionicons name="car" size={40} color={theme.colors.blue600} />
              </View>
              <Text style={styles.formTypeTitle}>تسليم المركبة</Text>
              <Text style={styles.formTypeTitleEn}>Vehicle Delivery</Text>
              <Text style={styles.formTypeDesc}>
                تسليم مركبة للسائق
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.formTypeCard, formType === 'receiving' && styles.selectedFormType]}
              onPress={() => handleFormTypeSelection('receiving')}
            >
              <View style={styles.formTypeIcon}>
                <Ionicons name="arrow-undo" size={40} color={theme.colors.success} />
              </View>
              <Text style={styles.formTypeTitle}>استلام المركبة</Text>
              <Text style={styles.formTypeTitleEn}>Vehicle Receiving</Text>
              <Text style={styles.formTypeDesc}>
                استلام مركبة من السائق
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.formTypeCard, formType === 'swap' && styles.selectedFormType]}
              onPress={() => handleFormTypeSelection('swap')}
            >
              <View style={styles.formTypeIcon}>
                <Ionicons name="swap-horizontal" size={40} color={theme.colors.warning} />
              </View>
              <Text style={styles.formTypeTitle}>تبديل المركبة</Text>
              <Text style={styles.formTypeTitleEn}>Vehicle Swap</Text>
              <Text style={styles.formTypeDesc}>
                تبديل مركبة بمركبة أخرى
              </Text>
            </TouchableOpacity>
          </View>
        </Card>

        {/* Quick Stats */}
        <Card style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>
              إحصائيات سريعة / Quick Stats
            </Text>
          </View>
          
          <View style={styles.statsGrid}>
            <View style={styles.statCard}>
              <Text style={styles.statNumber}>{availableVehicles.length}</Text>
              <Text style={styles.statLabel}>مركبات متاحة</Text>
              <Text style={styles.statLabelEn}>Available Vehicles</Text>
            </View>
            
            <View style={styles.statCard}>
              <Text style={styles.statNumber}>{assignedVehicles.length}</Text>
              <Text style={styles.statLabel}>مركبات مُعيَّنة</Text>
              <Text style={styles.statLabelEn}>Assigned Vehicles</Text>
            </View>
            
            <View style={styles.statCard}>
              <Text style={styles.statNumber}>{drivers.length}</Text>
              <Text style={styles.statLabel}>إجمالي السائقين</Text>
              <Text style={styles.statLabelEn}>Total Drivers</Text>
            </View>
          </View>
        </Card>

        {/* Recent Assignments Table */}
        <Card style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>
              آخر التعيينات / Recent Assignments
            </Text>
          </View>
          
          <ResponsiveTable
            data={assignedVehicles
              // Sort assigned vehicles by latest assignment date (newest first)
              .sort((a, b) => {
                const aAssignment = assignments
                  .filter(assignment => assignment.vehicle_id === a.id)
                  .sort((x, y) => new Date(y.created_at).getTime() - new Date(x.created_at).getTime())[0];
                const bAssignment = assignments
                  .filter(assignment => assignment.vehicle_id === b.id)
                  .sort((x, y) => new Date(y.created_at).getTime() - new Date(x.created_at).getTime())[0];
                
                const aDate = aAssignment ? new Date(aAssignment.created_at).getTime() : 0;
                const bDate = bAssignment ? new Date(bAssignment.created_at).getTime() : 0;
                
                return bDate - aDate; // Newest first
              })
              .map(vehicle => {
              const driver = drivers.find(d => d.id === vehicle.currentDriverId);
              // Find the latest assignment for this vehicle
              const latestAssignment = assignments
                .filter(a => a.vehicle_id === vehicle.id)
                .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
              
              // Determine status based on assignment history
              let statusText = 'مُعيَّن / Assigned';
              if (latestAssignment) {
                switch (latestAssignment.status) {
                  case 'active':
                    statusText = 'نشط / Active';
                    break;
                  case 'completed':
                    statusText = 'مكتمل / Completed';
                    break;
                  case 'cancelled':
                    statusText = 'ملغي / Cancelled';
                    break;
                  default:
                    statusText = 'مُعيَّن / Assigned';
                }
              }
              
              return {
                licensePlate: vehicle.licensePlate,
                makeModel: vehicle.makeModel,
                year: vehicle.year,
                driverName: driver?.name || 'غير محدد / Not Assigned',
                assignedDate: latestAssignment ? new Date(latestAssignment.assignment_date).toLocaleDateString('en-GB') : 'غير محدد / Not Set',
                status: statusText
              };
            })}
            columns={[
              { key: 'licensePlate', title: 'رقم اللوحة / Plate' },
              { key: 'makeModel', title: 'نوع المركبة / Make Model' },
              { key: 'year', title: 'السنة / Year' },
              { key: 'driverName', title: 'السائق / Driver' },
              { key: 'assignedDate', title: 'تاريخ التعيين / Date' },
              { key: 'status', title: 'الحالة / Status' }
            ]}
          />
        </Card>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.gray50,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing.lg,
    backgroundColor: theme.colors.white,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray200,
  },
  backButton: {
    padding: theme.spacing.sm,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
    textAlign: 'center',
    flex: 1,
  },
  historyButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.xs,
    padding: theme.spacing.sm,
    backgroundColor: '#e3f2fd', // Light blue background
    borderRadius: 8,
  },
  historyButtonText: {
    fontSize: 14,
    color: theme.colors.blue600, // Changed from theme.colors.primary to blue
    fontWeight: '500' as any,
  },
  content: {
    flex: 1,
    padding: theme.spacing.md,
  },
  scrollContent: {
    paddingBottom: theme.spacing.xl,
    flexGrow: 1,
  },
  section: {
    marginBottom: theme.spacing.lg,
  },
  sectionHeader: {
    backgroundColor: theme.colors.gray100,
    padding: theme.spacing.md,
    marginBottom: theme.spacing.md,
    borderRadius: 8,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
    textAlign: 'center',
  },
  formTypeGrid: {
    flexDirection: 'row',
    gap: theme.spacing.md,
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  formTypeCard: {
    flex: 1,
    minWidth: 150,
    maxWidth: 200,
    padding: theme.spacing.lg,
    borderWidth: 2,
    borderColor: theme.colors.gray300,
    borderRadius: 12,
    backgroundColor: theme.colors.white,
    alignItems: 'center',
  },
  selectedFormType: {
    borderColor: theme.colors.blue600, // Changed from primary
    backgroundColor: '#e3f2fd', // Light blue background
  },
  formTypeIcon: {
    marginBottom: theme.spacing.md,
  },
  formTypeTitle: {
    fontSize: 16,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
    textAlign: 'center',
    marginBottom: theme.spacing.xs,
  },
  formTypeTitleEn: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    textAlign: 'center',
    marginBottom: theme.spacing.sm,
  },
  formTypeDesc: {
    fontSize: 12,
    color: theme.colors.textSecondary,
    textAlign: 'center',
  },
  statsGrid: {
    flexDirection: 'row',
    gap: theme.spacing.md,
    justifyContent: 'space-around',
  },
  statCard: {
    flex: 1,
    alignItems: 'center',
    padding: theme.spacing.md,
    backgroundColor: theme.colors.gray50,
    borderRadius: 8,
  },
  statNumber: {
    fontSize: 32,
    fontWeight: '700' as any,
    color: theme.colors.blue600, // Changed from primary
    marginBottom: theme.spacing.xs,
  },
  statLabel: {
    fontSize: 14,
    fontWeight: '500' as any,
    color: theme.colors.textPrimary,
    textAlign: 'center',
  },
  statLabelEn: {
    fontSize: 12,
    color: theme.colors.textSecondary,
    textAlign: 'center',
  },
  list: {
    flex: 1,
    padding: theme.spacing.md,
  },
  listItem: {
    backgroundColor: theme.colors.white,
    borderRadius: 8,
    marginBottom: theme.spacing.sm,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  itemContent: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: theme.spacing.md,
  },
  itemInfo: {
    flex: 1,
  },
  itemTitle: {
    fontSize: 16,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.xs,
  },
  itemSubtitle: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    marginBottom: theme.spacing.xs,
  },
  itemStatus: {
    fontSize: 12,
    color: theme.colors.textSecondary,
  },
  searchContainer: {
    padding: theme.spacing.md,
    backgroundColor: theme.colors.white,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray200,
  },
  searchInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.gray50,
    borderRadius: 8,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
  },
  searchIcon: {
    marginRight: theme.spacing.sm,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    color: theme.colors.textPrimary,
    paddingVertical: theme.spacing.xs,
  },
  clearButton: {
    padding: theme.spacing.xs,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: theme.spacing.xl,
  },
  emptyText: {
    fontSize: 16,
    color: theme.colors.textSecondary,
    textAlign: 'center',
    marginTop: theme.spacing.md,
  },
  vehicleCard: {
    backgroundColor: theme.colors.white,
    borderRadius: 12,
    marginBottom: theme.spacing.sm,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    marginHorizontal: theme.spacing.md,
  },
  vehicleCardContent: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: theme.spacing.md,
  },
  vehicleMainInfo: {
    flex: 1,
  },
  vehiclePlate: {
    fontSize: 18,
    fontWeight: '700' as any,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.xs,
  },
  vehicleMakeModel: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    marginBottom: theme.spacing.xs,
  },
  vehicleCode: {
    fontSize: 12,
    color: theme.colors.textSecondary,
    marginBottom: theme.spacing.xs,
  },
  vehicleStatusInfo: {
    alignItems: 'flex-end',
    marginRight: theme.spacing.md,
  },
  statusBadge: {
    paddingHorizontal: theme.spacing.sm,
    paddingVertical: theme.spacing.xs,
    borderRadius: 12,
    marginBottom: theme.spacing.xs,
  },
  statusText: {
    fontSize: 12,
    fontWeight: '600' as any,
    color: theme.colors.white,
  },
  driverInfo: {
    fontSize: 12,
    color: theme.colors.textSecondary,
    textAlign: 'right',
  },
  refreshButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.primary + '15',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    gap: 4,
  },
  refreshButtonText: {
    fontSize: 12,
    color: theme.colors.primary,
    fontWeight: '500',
  },
});